import { z } from 'zod';

/**
 * The client's half of the gateway protocol (api-and-contracts: WebSocket protocol).
 *
 * The gateway owns the protocol: `apps/realtime-gateway/src/protocol/frames.ts` is the definition
 * and these schemas are written against it. They are **not** imported from there — an app may not
 * import another app, and the gateway's copy sits on `@ccp/shared-kernel`, a server-side package
 * the browser bundle must not carry. So the frames are stated twice, once per side, and the
 * duplication is deliberate rather than overlooked: both halves are small, they change together
 * inside `/v1`, and `docs/55-workspace/realtime.md` describes them once for both.
 *
 * Everything the socket hands over is parsed before anything reads it. A WebSocket is a boundary
 * like a request body: a frame the client has not validated is a shape the server claimed.
 */

const uuid = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

export const topicSchema = z
  .string()
  .regex(
    new RegExp(
      `^(agent:(me|${uuid})|interaction:${uuid}|queue:${uuid}|team:${uuid}|chat:${uuid}|tenant:config)$`,
    ),
    'unknown topic',
  );
export type Topic = z.infer<typeof topicSchema>;

/** The topic every authenticated client follows: a stale configuration shows the wrong queues. */
export const configTopic: Topic = 'tenant:config';
/** The agent's own topic, as asked for; the gateway answers with it resolved to an agent id. */
export const ownAgentTopic: Topic = 'agent:me';

/** The interaction topic for one interaction, which is how a screen pop starts following a call. */
export function interactionTopic(interactionId: string): Topic {
  return `interaction:${interactionId}`;
}

/**
 * The chat topic for one session, which is how a chat pane follows what is being said.
 *
 * A visitor's widget subscribes to the same topic from the other side; the gateway publishes one
 * notification to both, because a chat's events order behind its interaction (M5-02).
 */
export function chatTopic(sessionId: string): Topic {
  return `chat:${sessionId}`;
}

/**
 * The compact notification a frame carries.
 *
 * There is no payload, on purpose: the relay publishes on a channel per tenant, pub/sub has no
 * per-subscriber authorisation, and a payload there would reach every socket of the tenant. So a
 * notification says *that* an aggregate changed and where in its stream; what it changed to is read
 * back through `/v1`, which authorises every field.
 */
export const eventNotificationSchema = z.object({
  id: z.uuid(),
  type: z.string().min(1),
  aggregateType: z.string().min(1),
  aggregateId: z.uuid(),
  orderingKey: z.string().min(1),
  sequence: z.number().int().positive(),
  occurredAt: z.iso.datetime({ offset: true }),
  classification: z.enum(['public', 'internal', 'confidential', 'restricted']),
});
export type EventNotification = z.infer<typeof eventNotificationSchema>;

// ---- what the gateway sends --------------------------------------------------------------------

export const serverFrameSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('event'),
    topic: topicSchema,
    event: eventNotificationSchema,
    /** The gateway withheld intermediate events for this topic, so a jump is not a lost event. */
    coalesced: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('authenticated'),
    actorId: z.uuid(),
    expiresAt: z.iso.datetime({ offset: true }),
  }),
  z.object({ type: z.literal('heartbeat'), serverTime: z.iso.datetime({ offset: true }) }),
  z.object({ type: z.literal('subscribed'), topics: z.array(topicSchema) }),
  z.object({
    type: z.literal('resynced'),
    aggregates: z.array(
      z.object({ aggregateId: z.uuid(), throughSequence: z.number().int().nonnegative() }),
    ),
  }),
  z.object({
    type: z.literal('resync-required'),
    aggregates: z.array(z.uuid()),
    reason: z.enum(['beyond-retention', 'too-far-behind']),
  }),
  z.object({
    /**
     * The other side of a chat is typing. Relayed by the gateway and written nowhere: an
     * indicator that outlived the moment it described would be a lie.
     */
    type: z.literal('peer-typing'),
    topic: topicSchema,
    from: z.enum(['visitor', 'agent']),
    typing: z.boolean(),
  }),
  z.object({
    type: z.literal('error'),
    code: z.string().min(1),
    message: z.string(),
    details: z.unknown().optional(),
    correlationId: z.uuid().optional(),
  }),
]);
export type ServerFrame = z.infer<typeof serverFrameSchema>;

// ---- what the client sends ---------------------------------------------------------------------

/** One aggregate the client has fallen behind on, and where it got to. */
export interface ResyncRequest {
  readonly aggregateId: string;
  readonly sinceSequence: number;
}

export type ClientFrame =
  | { readonly type: 'auth'; readonly token: string; readonly as?: 'tenant' | 'visitor' }
  | {
      readonly type: 'subscribe';
      readonly topics: readonly Topic[];
      readonly lastSequences?: Readonly<Record<string, number>>;
    }
  | { readonly type: 'unsubscribe'; readonly topics: readonly Topic[] }
  | { readonly type: 'resync'; readonly aggregates: readonly ResyncRequest[] }
  | { readonly type: 'typing'; readonly topic: Topic; readonly typing: boolean }
  | { readonly type: 'heartbeat'; readonly deviceReady?: boolean };

/**
 * Parses one text frame.
 *
 * A frame the client cannot read is dropped, never thrown: the socket is the only way the workspace
 * learns anything, and taking it down over one unreadable frame would cost the agent their session
 * for a fault they cannot see. The gateway's rule is the mirror image — it closes a socket that
 * sends rubbish — and the asymmetry is deliberate: the server has a tenant's connection limit to
 * protect, the client has a call in progress.
 */
export function parseServerFrame(
  raw: string,
):
  | { readonly ok: true; readonly frame: ServerFrame }
  | { readonly ok: false; readonly why: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, why: 'the frame is not valid JSON' };
  }
  const result = serverFrameSchema.safeParse(parsed);
  if (!result.success) {
    return { ok: false, why: result.error.issues.map((issue) => issue.message).join('; ') };
  }
  return { ok: true, frame: result.data };
}
